import { useState, useEffect, useCallback } from 'react';
import {
  Customer,
  CustomerCreateParams,
  Subscription,
  SubscriptionUpdateParams,
  SubscriptionStatus,
  CheckoutParams,
  CheckoutSession,
} from '../types';
import { PaymentsConfig } from '../types/config';
import { PaymentProviderFactory } from '../../providers';
import { PaymentsError, PaymentErrorType } from '../types/errors';
import { ERROR_MESSAGES } from '../constants';
import { PaymentProvider } from '../types/provider';

interface UsePaymentsStore {
  config: PaymentsConfig | null;
  provider: PaymentProvider | null;
  initialized: boolean;
  loading: boolean;
  error: PaymentsError | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  customer: Customer | null;
  subscriptions: Subscription[];
  activeSubscription: Subscription | null;
  initialize: (config?: PaymentsConfig) => Promise<void>;
  createCustomer: (params: CustomerCreateParams) => Promise<Customer>;
  retrieveCustomer: (customerId: string) => Promise<Customer | null>;
  createCheckoutSession: (params: CheckoutParams) => Promise<CheckoutSession>;
  retrieveCheckoutSession: (sessionId: string) => Promise<CheckoutSession>;
  createSubscription: (params: {
    priceId: string;
    quantity?: number;
  }) => Promise<Subscription>;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  reactivateSubscription: (subscriptionId: string) => Promise<void>;
  listSubscriptions: (
    customerId: string,
    status?: SubscriptionStatus
  ) => Promise<Subscription[]>;
  retrieveSubscription: (
    subscriptionId: string
  ) => Promise<Subscription | null>;
  updateSubscription: (
    subscriptionId: string,
    params: SubscriptionUpdateParams
  ) => Promise<Subscription>;
  refreshCustomer: (customerId: string) => Promise<void>;
  refreshSubscriptions: (
    customerId: string,
    status?: SubscriptionStatus
  ) => Promise<void>;
  reset: () => void;
}

export const usePayments = (): UsePaymentsStore => {
  const [config, setConfig] = useState<PaymentsConfig | null>(null);
  const [provider, setProvider] = useState<PaymentProvider | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PaymentsError | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  const handleError = useCallback((err: PaymentsError) => {
    setError(err);
    console.error('Payments error:', err);
  }, []);

  const refreshSubscriptions = useCallback(
    async (customerId: string, status?: SubscriptionStatus) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const fetchedSubscriptions = await provider.listSubscriptions(
          customerId,
          status
        );
        setSubscriptions(fetchedSubscriptions);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const initialize = useCallback(
    async (initialConfig?: PaymentsConfig) => {
      setLoading(true);
      setError(null);
      try {
        const resolvedConfig =
          initialConfig || PaymentProviderFactory.autodetect();
        const newProvider = PaymentProviderFactory.create(resolvedConfig);
        setConfig(resolvedConfig);
        setProvider(newProvider);
        setInitialized(true);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
      } finally {
        setLoading(false);
      }
    },
    [handleError]
  );

  const retrieveCustomer = useCallback(
    async (customerId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const newCustomer = await provider.retrieveCustomer(customerId);
        setCustomer(newCustomer);
        return newCustomer;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const createCustomer = useCallback(
    async (params: CustomerCreateParams) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const newCustomer = await provider.createCustomer(params);
        setCustomer(newCustomer);
        return newCustomer;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const createSubscription = useCallback(
    async (params: { priceId: string; quantity?: number }) => {
      if (!provider || !customer) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const newSubscription = await provider.createSubscription({
          customerId: customer.id,
          items: [{ priceId: params.priceId, quantity: params.quantity }],
        });
        await refreshSubscriptions(customer.id);
        return newSubscription;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, customer, handleError, refreshSubscriptions]
  );

  const listSubscriptions = useCallback(
    async (customerId: string, status?: SubscriptionStatus) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const subs = await provider.listSubscriptions(customerId, status);
        setSubscriptions(subs);
        return subs;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const retrieveSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const sub = await provider.retrieveSubscription(subscriptionId);
        // Optionally refresh the list of subscriptions
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
        return sub;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, customer, handleError, refreshSubscriptions]
  );

  const createCheckoutSession = useCallback(
    async (params: CheckoutParams) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const session = await provider.createCheckoutSession(params);
        return session;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const retrieveCheckoutSession = useCallback(
    async (sessionId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const session = await provider.retrieveCheckoutSession(sessionId);
        return session;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const cancelSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        await provider.cancelSubscription(subscriptionId);
        // Refresh subscriptions after cancellation
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, customer, handleError, refreshSubscriptions]
  );

  const reactivateSubscription = useCallback(
    async (subscriptionId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        await provider.reactivateSubscription(subscriptionId);
        // Refresh subscriptions after reactivation
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, customer, handleError, refreshSubscriptions]
  );

  const updateSubscription = useCallback(
    async (subscriptionId: string, params: SubscriptionUpdateParams) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const updatedSubscription = await provider.updateSubscription(
          subscriptionId,
          params
        );
        // Refresh subscriptions after update
        if (customer?.id) {
          await refreshSubscriptions(customer.id);
        }
        return updatedSubscription;
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, customer, handleError, refreshSubscriptions]
  );

  const refreshCustomer = useCallback(
    async (customerId: string) => {
      if (!provider) {
        throw new PaymentsError(
          PaymentErrorType.PROVIDER_NOT_CONFIGURED,
          ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED
        );
      }
      setLoading(true);
      setError(null);
      try {
        const fetchedCustomer = await provider.retrieveCustomer(customerId);
        setCustomer(fetchedCustomer);
      } catch (err: unknown) {
        handleError(err as PaymentsError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [provider, handleError]
  );

  const reset = useCallback(() => {
    setConfig(null);
    setProvider(null);
    setInitialized(false);
    setLoading(false);
    setError(null);
    setCustomer(null);
    setSubscriptions([]);
  }, []);

  const activeSubscription =
    subscriptions.find(s => s.status === 'active' || s.status === 'trialing') ||
    null;

  // Initial fetch for customer and subscriptions if a customerId is provided
  useEffect(() => {
    // This useEffect should be triggered by the PaymentsProvider, not directly here
    // The PaymentsProvider will call `initialize` with the config.
    // If a customer ID is available in the config or passed to initialize,
    // then refreshCustomer and refreshSubscriptions can be called.
    // For now, removing the auto-fetch logic from here to avoid circular dependencies
    // and to align with the PaymentsProvider's role in initialization.
  }, []);

  return {
    config,
    provider,
    initialized,
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    customer,
    subscriptions,
    activeSubscription,
    initialize,
    createCustomer,
    retrieveCustomer,
    createCheckoutSession,
    retrieveCheckoutSession,
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    listSubscriptions,
    retrieveSubscription,
    updateSubscription,
    refreshCustomer,
    refreshSubscriptions,
    reset,
  };
};
